/**
 * Pure formatting helpers behind {@link HumanizedData} — the human-readable
 * rendering of agent-visible metadata (tool inputs/outputs, event payloads).
 *
 * Lossless by contract: these rows are the record of what the bot did, so the
 * format changes and the content never does. Nothing in this module truncates,
 * caps, or drops data.
 */

/** "optionIds" → "Option ids"; "latency_ms" → "Latency ms"; "returned-ids" → "Returned ids". */
export function humanizeKey(key: string): string {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * A leaf value as display text. Booleans read as words; null/undefined/"" render
 * an explicit "—" rather than disappearing — an empty field is information.
 */
export function formatPrimitive(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "yes" : "no";
  const text = String(value);
  return text === "" ? "—" : text;
}

/** True for anything that isn't an object/array — renderable as one inline value. */
export function isPlainPrimitive(value: unknown): boolean {
  return value === null || value === undefined || typeof value !== "object";
}

/** A string long enough (or multiline) to read as prose rather than a value. */
export function isProse(value: string): boolean {
  return value.includes("\n") || value.length > 160;
}

/** An all-primitive array reads as one comma-joined line — every item, no cap. */
export function joinPrimitives(values: unknown[]): string {
  return values.map(formatPrimitive).join(", ");
}

/**
 * Header for one item in an array-of-objects block. A header only — every
 * field of the item (the header's own field included) still renders in the
 * item's body, so nothing is summarized away.
 */
export function itemHeading(item: Record<string, unknown>, index: number): string {
  const candidate = [item["label"], item["name"], item["id"]].find((value) => typeof value === "string" || typeof value === "number");
  return candidate !== undefined ? String(candidate) : `#${index + 1}`;
}
